import React from 'react';
import { motion } from 'framer-motion';
import { Hammer, Plus, Shield, Sparkles } from 'lucide-react';

const BuildPage: React.FC = () => {
  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-accent-wood/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Hammer className="w-10 h-10 text-accent-wood" />
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">
            Build Your <span className="text-accent-wood">Legacy</span>
          </h1>
          <p className="text-xl text-primary-300 max-w-3xl mx-auto">
            Create and tokenize your precious items with our intuitive tools. Build provenance and share memories with your family.
          </p>
        </motion.div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Create New Item */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8 hover:shadow-xl transition-shadow"
          >
            <div className="w-16 h-16 bg-accent-wood/20 rounded-xl flex items-center justify-center mb-6">
              <Plus className="w-8 h-8 text-accent-wood" />
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-4">
              Create New Item
            </h3>
            <p className="text-primary-300 mb-6">
              Start by adding a new precious item to your legacy collection.
            </p>
            <button className="w-full bg-accent-wood hover:bg-accent-wood-light text-white py-3 rounded-xl font-semibold transition-colors">
              Get Started
            </button>
          </motion.div>

          {/* Upload Photos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 rounded-2xl border border-accent-wood/30 p-8 hover:shadow-xl transition-shadow"
          >
            <div className="w-16 h-16 bg-accent-wood/20 rounded-xl flex items-center justify-center mb-6">
              <Sparkles className="w-8 h-8 text-accent-wood" />
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-4">
              Upload Photos
            </h3>
            <p className="text-primary-300 mb-6">
              Add high-quality images of your precious items and memories.
            </p>
            <button className="w-full bg-accent-wood hover:bg-accent-wood-light text-white py-3 rounded-xl font-semibold transition-colors">
              Upload Now
            </button>
          </motion.div>

          {/* Add Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8 hover:shadow-xl transition-shadow"
          >
            <div className="w-16 h-16 bg-accent-wood/20 rounded-xl flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-accent-wood" />
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-4">
              Add Details
            </h3>
            <p className="text-primary-300 mb-6">
              Document the story, history, and sentimental value of your items.
            </p>
            <button className="w-full bg-accent-wood hover:bg-accent-wood-light text-white py-3 rounded-xl font-semibold transition-colors">
              Document
            </button>
          </motion.div>
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Building <span className="text-accent-wood">Provenance</span>
            </h2>
            <p className="text-primary-300 text-lg">
              Content coming soon... This section will explain how to build and track the provenance of your valuable items.
            </p>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 rounded-2xl border border-accent-wood/30 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-white mb-4">
              Creating <span className="text-accent-wood">Collections</span>
            </h2>
            <p className="text-primary-300 text-lg">
              Content coming soon... This section will show how to organize your items into meaningful collections.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BuildPage; 